import { useMemo } from "react";

import styled, { css } from "styled-components";

import { Stack } from "@similarweb/ui-components/foundations";

import { useTranslate } from "@similarweb/i18n-provider";

import { AllowedValuesExcelDownload } from "app/pages/sales-intelligence/common-components/export-table/components/AllowedValuesExcelDownload";
import { CrmCell } from "app/pages/sales-intelligence/common-components/export-table/cells/CrmCell";
import { DeleteCell } from "app/pages/sales-intelligence/common-components/export-table/cells/DeleteCell/DeleteCell";
import { ExampleCell } from "app/pages/sales-intelligence/common-components/export-table/cells/ExampleCell";
import { OverwriteCell } from "app/pages/sales-intelligence/common-components/export-table/cells/OverwriteCell";
import { SimilarwebCell } from "app/pages/sales-intelligence/common-components/export-table/cells/SimilarwebCell";
import { ADD_FIELD } from "app/pages/sales-intelligence/common-components/export-table/const";
import { INTERNAL_ADD_ITEM_TEXT } from "app/pages/sales-intelligence/common-components/export-table/constants";
import { createExportTableRenderer } from "app/pages/sales-intelligence/common-components/export-table/exportTableRenderer";
import {
    StyledEmptyStateWrapper,
    StyledTable as BaseStyledTable,
} from "app/pages/sales-intelligence/common-components/export-table/styles";
import type { CrmFieldType } from "app/pages/sales-intelligence/common-components/export-table/types";
import {
    createPlacement,
    getSwFieldColumnName,
    isPreDefinedField,
    isPrimaryMandatoryFieldInstance,
} from "app/pages/sales-intelligence/common-components/export-table/utils";
import TechnographicsCell from "app/pages/sales-intelligence/modules/technology/components/TechnographicsCell";
import usePrefetchTechnologiesDescription from "app/pages/sales-intelligence/modules/technology/hooks/usePrefetchTechnologiesDescription";
import type {
    ExportItem,
    SalesforcePreDefinedType,
} from "app/pages/sales-intelligence/sub-modules/management/store/type";
import { convertIntegrationTypeForApi } from "app/pages/sales-intelligence/sub-modules/management/utils/utils";
import Table from "app/pages/workspace/sales/components/custom-table/Table/Table";

import useCrmFieldsProvider from "../../../hooks/useCrmFieldsProvider";
import useMappingTableData from "../../../hooks/useMappingTableData";
import useMappingTableFocus from "../../../hooks/useMappingTableFocus";
import useMappingTableHandlers from "../../../hooks/useMappingTableHandlers";
import { getMandatoryFieldKeys, isEnrichmentOverwriteSwitchLocked } from "../../../utils/defaultMappings";
import CrmEnrichmentJobWizardHeader from "../CrmEnrichmentJobWizardHeader";

import type { FieldsMappingStepProps } from "./types";

const EDITOR_TABLE_HEIGHT = "calc(100vh - 220px)";

const StyledFieldsMappingRoot = styled(Stack)<{ $isWizardStep: boolean }>`
    ${({ $isWizardStep }) =>
        $isWizardStep &&
        css`
            flex: 1;
            min-height: 0;
            height: 100%;
        `}
`;

const StyledFieldsMappingStep = styled(Stack)<{ $isWizardStep: boolean }>`
    flex: 1;
    min-height: 0;

    ${({ $isWizardStep }) =>
        !$isWizardStep &&
        css`
            height: ${EDITOR_TABLE_HEIGHT};
        `}
`;

const StyledTable = styled(BaseStyledTable)`
    flex: 1;
    min-height: 0;
`;

const CrmEnrichmentFieldsMappingWizardStep = ({
    crmProvider,
    enrichmentObject,
    jobType,
    mappings,
    columns,
    isColumnsLoading,
    onAddMapping,
    onRemoveMapping,
    onUpdateMapping,
    showHeader = true,
}: FieldsMappingStepProps) => {
    const { translate } = useTranslate();

    usePrefetchTechnologiesDescription();

    const { exportName, tableData, dataLength, similarwebFields, selectedCrmFields } = useMappingTableData({
        crmProvider,
        enrichmentObject,
        jobType,
        mappings,
    });

    const { crmOptions, isCrmFieldsLoading, fetchCrmFields } = useCrmFieldsProvider(crmProvider);

    const { focusIdx, setManuallyFocusedIdx, activeFocusIdx } = useMappingTableFocus(mappings);

    const crmColumns = columns as CrmFieldType[];

    const {
        onSimilarwebCellClick,
        onCrmCellClick,
        onToggleClick,
        onExampleChange,
        handleDeleteClick,
        getCrmFields,
        searchFilter,
    } = useMappingTableHandlers({
        crmProvider,
        tableData,
        columns: crmColumns,
        onAddMapping,
        onRemoveMapping,
        onUpdateMapping,
        fetchCrmFields,
    });

    const exportTableService = useMemo(
        () => createExportTableRenderer(translate, crmProvider, exportName),
        [translate, crmProvider, exportName],
    );

    const isPrimaryMandatoryField = useMemo(() => {
        const mandatoryFields = getMandatoryFieldKeys(crmProvider, enrichmentObject);
        return (setting: ExportItem): boolean => isPrimaryMandatoryFieldInstance(setting, tableData, mandatoryFields);
    }, [crmProvider, enrichmentObject, tableData]);

    const tableColumns = useMemo(
        () => exportTableService.getColumns({ ignoredColumns: ["forExport", "forEnrichment"] }),
        [exportTableService],
    );

    const renderSimilarwebField = (setting: ExportItem, index: number) => {
        const { title } = setting;
        const isRequired = isPrimaryMandatoryField(setting);
        const { placement } = createPlacement(dataLength, index);

        return (
            <SimilarwebCell
                index={index}
                exportName={exportName}
                setting={setting}
                similarwebFields={similarwebFields}
                onClick={onSimilarwebCellClick}
                title={title}
                searchFilter={searchFilter}
                isRequired={isRequired}
                placement={placement}
            />
        );
    };

    const renderExample = (setting: ExportItem, index: number) => {
        const { example, metadata, externalField } = setting;
        const isVisible = externalField !== INTERNAL_ADD_ITEM_TEXT;
        const isDisabled = !externalField;

        return isVisible ? (
            <ExampleCell
                key={index}
                onClick={() => setManuallyFocusedIdx(index)}
                onBlur={() => setManuallyFocusedIdx(null)}
                focusIdx={activeFocusIdx}
                index={index}
                tableLength={dataLength}
                exportName={exportName}
                externalField={externalField}
                setting={setting}
                example={example}
                value={metadata}
                onChange={(value) => onExampleChange(setting, value)}
                isDisabled={isDisabled}
                columns={crmColumns}
                TechnographicsCellComponent={TechnographicsCell}
            />
        ) : null;
    };

    const renderCrm = (setting: ExportItem, index: number) => {
        const { externalField, sourceType } = setting;
        const isPreDefined = isPreDefinedField(setting);
        const swColumnName = getSwFieldColumnName(setting);

        const handleToggle = (isOpen: boolean) => {
            if (!isOpen) {
                setManuallyFocusedIdx(null);
                return;
            }
            if (index !== focusIdx) {
                setManuallyFocusedIdx(index);
            }

            getCrmFields(
                swColumnName,
                sourceType,
                isPreDefined,
                convertIntegrationTypeForApi(exportName) as SalesforcePreDefinedType,
            );
        };

        return (
            <CrmCell
                onClick={(newSetting) => onCrmCellClick(setting, newSetting)}
                handleToggle={handleToggle}
                index={index}
                focusIdx={activeFocusIdx}
                exportName={exportName}
                tableLength={dataLength}
                isPreDefined={isPreDefined}
                isLoading={isCrmFieldsLoading}
                isColumnsLoading={isColumnsLoading}
                field={setting.field}
                selectedCrmField={externalField}
                options={crmOptions}
                columns={crmColumns}
                selectedCrmFields={selectedCrmFields}
                groupByRequired={false}
                valueType={setting.valueType}
            />
        );
    };

    const renderOverwrite = (setting: ExportItem) => {
        const { field, overwrite, metadata = "" } = setting;
        const isOverwriteLocked =
            (isPrimaryMandatoryField(setting) &&
                isEnrichmentOverwriteSwitchLocked(crmProvider, enrichmentObject, metadata)) ||
            field === ADD_FIELD;
        const tooltipContent = exportTableService.getOverwriteCellTooltipContent(overwrite);

        return (
            <OverwriteCell
                setting={setting}
                isRequired={isOverwriteLocked}
                onClick={(s, value) => onToggleClick({ setting: s, key: "overwrite", value })}
                overwrite={overwrite}
                tooltipContent={tooltipContent}
            />
        );
    };

    const renderDelete = (setting: ExportItem) => {
        const { field } = setting;
        const isVisible = !isPrimaryMandatoryField(setting) && field !== ADD_FIELD;

        if (!isVisible) {
            return null;
        }

        return (
            <DeleteCell
                setting={setting}
                handleDelete={handleDeleteClick}
            />
        );
    };

    return (
        <StyledFieldsMappingRoot
            $isWizardStep={showHeader}
            gap={showHeader ? "8px" : "12px"}
        >
            {showHeader ? (
                <Stack gap="8px">
                    <CrmEnrichmentJobWizardHeader
                        titleKey="si.crm-enrichment.job-wizard.fields-mapping.title"
                        descriptionKey="si.crm-enrichment.job-wizard.fields-mapping.description"
                    />
                    <AllowedValuesExcelDownload
                        crmProvider={crmProvider}
                        exportName={exportName}
                    />
                </Stack>
            ) : null}

            <StyledFieldsMappingStep
                $isWizardStep={showHeader}
                gap="0"
            >
                <StyledTable data-automation="crm-enrichment-mapping-table">
                    <Table
                        hasStickyHeader
                        emptyState={
                            <StyledEmptyStateWrapper>
                                {translate("si.crm-enrichment.job-wizard.fields-mapping.empty")}
                            </StyledEmptyStateWrapper>
                        }
                        data={tableData}
                        columns={tableColumns}
                        customRenderers={{
                            title: renderSimilarwebField,
                            example: renderExample,
                            externalField: renderCrm,
                            overwrite: renderOverwrite,
                            delete: renderDelete,
                        }}
                        customHeaderRenderers={{
                            title: exportTableService.renderCustomHeaderCell("title", dataLength),
                            externalField: exportTableService.renderCustomHeaderCell("externalField"),
                            overwrite: exportTableService.renderCustomHeaderCell("overwrite"),
                            example: exportTableService.renderCustomHeaderCell("example"),
                        }}
                    />
                </StyledTable>
            </StyledFieldsMappingStep>
        </StyledFieldsMappingRoot>
    );
};

export default CrmEnrichmentFieldsMappingWizardStep;
